<script setup lang="ts">
import { computed, getCurrentInstance, type ComponentPublicInstance } from 'vue'
import { NConfigProvider, NMessageProvider, darkTheme, dateZhCN, zhCN } from 'naive-ui'
import { themeOverridesFor } from '../theme'
import { isDarkTone, type Tone } from '../tokens'
import { useToneVars } from '../tone-vars'
import type { NavItem } from '../types'
import NavIcon from './NavIcon.vue'

/**
 * 整体骨架:左侧导航 + 内容区(v2 的壳,S-41 按画板重排)。
 *
 * ═══ 它长什么样、为什么 ═══
 *
 * 2026-10-16 的改图轮把两个后台端画成了同一个壳:220 宽的深色侧栏 ——
 * 顶部品牌块(方块 + 端名)、八项以内的导航、底部海报位/账号与登出;
 * 内容区直接顶到页头。**没有全局顶栏** —— 旧版那条 60px 的顶栏(面包屑 +
 * 徽标 + 退出)在两端的画板上都不存在:面包屑能说的「你在哪」侧栏的激活态已经在说,
 * 而徽标与登出更适合挂在侧栏底部那块「属于谁」的区域里。
 *
 * 导航项的激活态是**填充圆角块 + 白字**(画板如此),旧版那条 3px 左竖条撤掉了 ——
 * 两种「选中」语言同屏出现时,页面上只是「这一条多了一点东西」,没人会报它。
 *
 * ═══ 为什么这里不 import vue-router ═══
 *
 * 侧栏要高亮当前页,而当前页只有路由知道。这个包的 dependencies 只有 vue 与
 * naive-ui(规格 §4.0),引 vue-router 会多出一个依赖,而它在两个端里各有一份实例,
 * 成了第三份。
 *
 * 办法是读 `$route` —— vue-router 安装时把它挂在 `globalProperties` 上,
 * 通过组件实例就能拿到,不需要 import 它的任何符号(那样 typecheck 与
 * 打包器才算「引了这个依赖」)。读的时候是响应式的:vue-router 用
 * getter + shallowRef 实现它,在 computed 里读会正常建立依赖。
 *
 * 万一宿主没有装路由(比如这个包被单拎出来跑),退回 `location.pathname`:
 * 侧边栏的高亮仍然是对的,而不是整块空着。
 *
 * 它是**唯一**内含 `n-config-provider` 与 `n-message-provider` 的地方(与
 * LoginLayout 并列)—— 两个端的 App.vue 里因此不再有 provider 那一层,视图是纯内容。
 */
interface WithRoute {
  readonly $route?: { readonly path?: unknown }
}

const props = withDefaults(
  defineProps<{
    /** 'light' | 'dark' | 'ops-dark'。深色系(:dark / :ops-dark)都走 darkTheme */
    tone?: Tone
    /** 端名,如「运营后台」/「监管端」。它现在只出现在侧栏品牌块上 */
    product: string
    nav: readonly NavItem[]
    /** 侧栏底部的徽标,如「全程只读」(监管端) */
    badge?: string
    userLabel?: string
    /**
     * 侧栏底部的海报位(画板:180×110 图 + 图下两行字)。运营端在用;
     * 字与图都从 App.vue 传,免得「同一句海报文案」在组件与视图里各有一份。
     */
    poster?: { readonly src: string; readonly title: string; readonly subtitle: string }
  }>(),
  { tone: 'light' },
)

const emit = defineEmits<{ logout: [] }>()

/** 变量同时挂到根元素与 documentElement 上,原因见 tone-vars.ts */
const vars = useToneVars(() => props.tone)

/** 深色系统一判据:dark 与 ops-dark 都用 naive 的深色主题(见 tokens.ts 的 isDarkTone) */
const dark = computed(() => isDarkTone(props.tone))

const instance = getCurrentInstance()

const currentPath = computed<string>(() => {
  const proxy = instance?.proxy as (ComponentPublicInstance & WithRoute) | null
  const path = proxy?.$route?.path
  return typeof path === 'string' ? path : window.location.pathname
})

/** 前缀匹配要带一个分隔符,否则 `/orders` 会把 `/orders-archive` 也算成自己 */
function isActive(item: NavItem): boolean {
  return currentPath.value === item.to || currentPath.value.startsWith(`${item.to}/`)
}
</script>

<template>
  <n-config-provider
    :theme="dark ? darkTheme : null"
    :theme-overrides="themeOverridesFor(props.tone)"
    :locale="zhCN"
    :date-locale="dateZhCN"
  >
    <n-message-provider>
      <div class="shell" :style="vars">
        <aside class="sidebar">
          <div class="sidebar-inner">
            <div class="brand">
              <span class="brand-mark"><NavIcon name="plane" :size="16" /></span>
              <span class="brand-text">{{ props.product }}</span>
            </div>

            <nav class="nav">
              <!-- 只列已经能点通的链路:空菜单项会让人以为功能有了,点进去才发现是空的。
                   这条纪律由 guards/check-admin-nav.mjs 双向核对(NAV ↔ 路由 ↔ 视图文件) -->
              <router-link
                v-for="item in props.nav"
                :key="item.key"
                class="nav-item"
                :class="{ active: isActive(item) }"
                :to="item.to"
              >
                <NavIcon :name="item.icon" :size="18" />
                <span class="nav-label">{{ item.label }}</span>
              </router-link>
            </nav>

            <figure v-if="props.poster" class="poster">
              <img class="poster-img" :src="props.poster.src" alt="" />
              <figcaption class="poster-text">
                <span class="poster-title">{{ props.poster.title }}</span>
                <span class="poster-sub">{{ props.poster.subtitle }}</span>
              </figcaption>
            </figure>

            <div class="foot">
              <span v-if="props.badge" class="badge">{{ props.badge }}</span>
              <span v-if="props.userLabel" class="user">{{ props.userLabel }}</span>
              <button class="logout" type="button" @click="emit('logout')">
                <NavIcon name="logout" :size="16" />
                <span class="logout-text">退出</span>
              </button>
            </div>
          </div>
        </aside>

        <div class="main">
          <main class="content">
            <!--
              key 用当前路径:路由一变就换掉这棵子树,160ms 的淡入上移才会重新播。
              换成 CSS transition 的话,得在路由钩子里手动加/删类,那份状态迟早会漏。
            -->
            <div :key="currentPath" class="content-inner">
              <slot />
            </div>
          </main>
        </div>
      </div>
    </n-message-provider>
  </n-config-provider>
</template>

<style scoped>
.shell {
  display: flex;
  min-height: 100vh;
  background: var(--sd-bg);
  color: var(--sd-text1);
  font-family: var(--sd-font-sans);
}

/* 220 宽、通高渐变底、**没有描边**(画板:侧栏与内容区靠色差分界,不靠一条线) */
.sidebar {
  flex: none;
  width: 220px;
  z-index: 1;
  background: var(--sd-sidebar-bg);
}

/*
 * 侧边栏分两层:外层是**参与布局的一列**(宽度就是它的宽度,背景跟着整页长),
 * 内层才是 sticky 的那块(高度锁一屏,滚动时停在视口里)。
 *
 * 为什么不直接用 `position: fixed`:固定定位的元素在**整页截图**里只覆盖
 * 第一个视口的高度 —— 长页面截出来,侧边栏会在半途断掉,下面露出一条别色的底。
 * 而那正是这个项目验收 UI 的方式(截图看),所以这个「只在截图里存在」的断层
 * 会被当成真缺陷反复报上来。做成布局列之后,截图与真人看到的是一样的。
 */
.sidebar-inner {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 48px;
  flex: none;
  color: var(--sd-sidebar-text-active);
}

.brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: none;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-brand-mark);
  color: var(--sd-sidebar-text-active);
}

.brand-text {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
}

/* 导航占住中间的弹性区:底部那块(海报 / 账号 / 退出)因此总贴在底边 */
.nav {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  margin-top: 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 0 12px;
  flex: none;
  border-radius: var(--sd-radius-sm);
  color: var(--sd-sidebar-text);
  font-size: 14px;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease;
}

/* 选中态 = 填充圆角块 + 白字(画板)。旧版那条 3px 左竖条已撤 —— 两种「选中」
   语言同屏时页面上只是「这一条多了一点东西」,没有任何东西会报它 */
.nav-item:hover,
.nav-item.active {
  color: var(--sd-sidebar-text-active);
  background: var(--sd-sidebar-active-bg);
}

.nav-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.poster {
  margin: 0;
  flex: none;
}

.poster-img {
  display: block;
  width: 100%;
  height: 110px;
  object-fit: cover;
  border-radius: var(--sd-radius-sm);
}

.poster-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}

.poster-title {
  font-size: 12px;
  color: var(--sd-sidebar-text-active);
}

.poster-sub {
  font-size: 10px;
  color: var(--sd-sidebar-text);
}

.foot {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.badge {
  align-self: flex-start;
  font-size: 12px;
  line-height: 22px;
  padding: 0 10px;
  border-radius: var(--sd-radius-pill);
  color: var(--sd-brand);
  background: var(--sd-brand-soft);
  border: 1px solid var(--sd-brand-border);
  white-space: nowrap;
}

.user {
  font-size: 12.5px;
  color: var(--sd-sidebar-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 登出:长得像一条导航项(同一套高度与圆角),但它是按钮不是链接 */
.logout {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-sidebar-text);
  font-size: 14px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease;
}

.logout:hover {
  color: var(--sd-sidebar-text-active);
  background: var(--sd-sidebar-active-bg);
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  min-width: 0;
  padding: 24px;
}

.content-inner {
  max-width: 1280px;
  margin: 0 auto;
  animation: sd-content-in 160ms ease-out;
}

@keyframes sd-content-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* 窄屏:侧栏收成 64px 只留图标。留 220px 的话内容区在 1280 的屏上只剩一半宽。
   海报与徽标在这一档整块收起 —— 64px 里塞不下它们,硬压只会得到一条挤满字的窄条 */
@media (max-width: 1023px) {
  .sidebar {
    width: 64px;
  }

  .sidebar-inner {
    padding: 12px 8px;
  }

  .brand {
    justify-content: center;
  }

  .brand-text,
  .nav-label,
  .logout-text,
  .user,
  .badge,
  .poster {
    display: none;
  }

  .nav-item,
  .logout {
    justify-content: center;
    padding: 0;
  }
}
</style>
